import React, { useState, useCallback } from "react";
import { CarOutlined, EnvironmentOutlined, SwapLeftOutlined, SwapRightOutlined } from "@ant-design/icons";
import { Modal, Row, Divider, Radio, Switch } from "antd";
import { connect } from "dva";
import { DealerExchangeType } from "@/utils/enum";

function showError() {
  Modal.error({
    content:
      "Could not create dealer exchange. Please try again later or call Autosled support if this continues to happen.",
    style: { fontWeight: "bold" },
    width: "25%",
  });
}

const DealerExchangeModal = ({
  isAdmin,
  hideModal,
  dealerExchangeModalVisible,
  adminTradeModalVisible,
  loadingCreateDealerExchange,
  loadingAdminCreateDealerExchange,
  shipmentId,
  dispatch,
  onSuccess,
}) => {
  const [dealerExchangeType, setDealerExchangeType] = useState(DealerExchangeType.Ordered);
  const [isExchangeRequired, setIsExchangeRequired] = useState(false);

  const onClose = useCallback(() => {
    setDealerExchangeType(DealerExchangeType.Ordered);
    setIsExchangeRequired(false);
    hideModal?.();
  }, [hideModal]);

  const handleRequiredToggleChange = useCallback(checked => {
    setIsExchangeRequired(checked);
  }, []);

  const onTradeTypeChange = useCallback(event => {
    const { value } = event.target;
    setDealerExchangeType(value);
    setIsExchangeRequired(false);
  }, []);

  const handleCreate = useCallback(() => {
    const type = isAdmin ? "adminShipmentView/createDealerExchange" : "shipmentDetails/createDealerExchange";

    dispatch({
      type,
      shipmentId,
      dealerExchangeType,
      isExchangeRequired,
    })
      .then(response => {
        onClose();
        if (response?.error) {
          showError();
        } else {
          Modal.success({
            content: "Successfully created new dealer exchange.",
            style: { fontWeight: "bold" },
            width: "25%",
          });
          onSuccess?.(isExchangeRequired);
        }
      })
      .catch(err => {
        onClose();
        console.error(err);
        showError();
      });
  }, [isAdmin, shipmentId, dealerExchangeType, isExchangeRequired, dispatch, onClose, onSuccess]);

  return (
    <Modal
      title="Create Dealer Trade"
      open={isAdmin ? adminTradeModalVisible : dealerExchangeModalVisible}
      okText="Create"
      onOk={handleCreate}
      onCancel={onClose}
      confirmLoading={isAdmin ? loadingAdminCreateDealerExchange : loadingCreateDealerExchange}
    >
      <div>
        {`This will create a return shipment with this shipment's pickup and dropoff information reversed.
          Both shipments in a trade must be posted at the same time.
          Any transporter that dispatches this shipment will also be offered the return shipment.
          If they decline the offer, the return shipment will remain posted.`}
      </div>

      <div style={{ marginTop: 16 }}>
        {`Dealer trades can be one of two types: `}
        <strong>Ordered</strong>
        {` or `}
        <strong>Unordered</strong>
      </div>

      <div style={{ marginTop: 16 }}>
        {`An `}
        <strong>Ordered</strong>
        {` trade will require the first leg of the trade to be delivered before the second leg can be picked up.`}
      </div>

      <div style={{ marginTop: 16 }}>
        {`An `}
        <strong>Unordered</strong>
        {` trade will allow the first and second legs of the trade to be dispatched, picked up, and delivered in any order.`}
      </div>

      <div style={{ marginTop: 16 }}>
        Please select the type of trade, and then click the &quot;Create&quot; button.
      </div>

      <div style={{ marginTop: 16 }}>Trade Type:</div>

      <Radio.Group value={dealerExchangeType} onChange={onTradeTypeChange} size="small" style={{ marginBottom: 16 }}>
        <Radio value={DealerExchangeType.Ordered}>
          <strong>Ordered</strong>
        </Radio>
        <Radio value={DealerExchangeType.Unordered}>
          <strong>Unordered</strong>
        </Radio>
      </Radio.Group>

      {/* Conditionally render On/Off Toggle */}
      {isAdmin && dealerExchangeType === DealerExchangeType.Ordered && (
        <div style={{ marginTop: 16 }}>
          <span>Dealer Exchange Required:</span>
          <Switch checked={isExchangeRequired} onChange={handleRequiredToggleChange} style={{ marginLeft: 8 }} />
        </div>
      )}

      {/* Current Shipment */}
      <Divider orientation="left">
        {`This Shipment ${dealerExchangeType === DealerExchangeType.Ordered ? "(goes first)" : ""}`}
      </Divider>

      <Row justify="space-around" style={{ marginTop: 32, marginBottom: 32 }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            flexDirection: "column",
          }}
        >
          <div style={{ color: "red", fontWeight: "bold" }}>A</div>
          <EnvironmentOutlined style={{ fontSize: "2vw", color: "red" }} color="red" />
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            flexDirection: "column",
            justifyContent: "flex-end",
          }}
        >
          <SwapRightOutlined style={{ fontSize: "2vw" }} />
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            flexDirection: "column",
            justifyContent: "flex-end",
          }}
        >
          <CarOutlined style={{ fontSize: "2vw", color: "red" }} />
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            flexDirection: "column",
            justifyContent: "flex-end",
          }}
        >
          <SwapRightOutlined style={{ fontSize: "2vw" }} />
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            flexDirection: "column",
          }}
        >
          <div style={{ color: "blue", fontWeight: "bold" }}>B</div>
          <EnvironmentOutlined style={{ fontSize: "2vw", color: "blue" }} color="blue" />
        </div>
      </Row>

      {/* Return Shipment */}
      <Divider orientation="left">
        {`Return Shipment ${dealerExchangeType === DealerExchangeType.Ordered ? "(goes second)" : ""}`}
      </Divider>

      <Row justify="space-around" style={{ marginTop: 32, marginBottom: 32 }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            flexDirection: "column",
          }}
        >
          <div style={{ color: "red", fontWeight: "bold" }}>A</div>
          <EnvironmentOutlined style={{ fontSize: "2vw", color: "red" }} color="red" />
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            flexDirection: "column",
            justifyContent: "flex-end",
          }}
        >
          <SwapLeftOutlined style={{ fontSize: "2vw" }} />
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            flexDirection: "column",
            justifyContent: "flex-end",
          }}
        >
          <CarOutlined style={{ fontSize: "2vw", color: "blue" }} />
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            flexDirection: "column",
            justifyContent: "flex-end",
          }}
        >
          <SwapLeftOutlined style={{ fontSize: "2vw" }} />
        </div>

        <div
          style={{
            display: "flex",
            alignItems: "center",
            flexDirection: "column",
          }}
        >
          <div style={{ color: "blue", fontWeight: "bold" }}>B</div>
          <EnvironmentOutlined style={{ fontSize: "2vw", color: "blue" }} color="blue" />
        </div>
      </Row>
    </Modal>
  );
};

export default connect(({ shipmentDetails, adminShipmentView, loading }) => ({
  dealerExchangeModalVisible: shipmentDetails?.dealerExchangeModalVisible,
  adminTradeModalVisible: adminShipmentView?.dealerExchangeModalVisible,
  loadingCreateDealerExchange: loading.effects["shipmentDetails/createDealerExchange"],
  loadingAdminCreateDealerExchange: loading.effects["adminShipmentView/createDealerExchange"],
}))(DealerExchangeModal);
